import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  Input,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import {
  PhasedAmounts,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
  perUnit,
} from "./threeMShared";
import { fmtMonthLong } from "../MonthlyPnl/pnlShared";

// What a single month of the programme is actually made of. Opened from a
// monthly total in the Time Phasing grid's Project Total row: that grid shows
// one measure at a time, so a month's composition is otherwise invisible.
//
// Deliberately month-wise, not phase-wise — the question is "what is September
// made of"; the tree underneath already answers which phases spend in September.
//
// The item detail comes from the EXISTING Monthly P&L month-detail endpoint,
// which is the itemised form of the very sums the phasing cells aggregate. It
// reconciles with the clicked total to the paisa on every pillar (verified on
// live data), so no new backend was needed — but that also means the two must
// stay in step: if either side's source arms change, they change together.

// Same hues the Planned vs Actual group headers use, so a colour means the same
// M everywhere in this report.
const PILLARS: Array<{
  key: keyof PhasedAmounts;
  label: string;
  color: string;
}> = [
  { key: "material", label: "Material", color: "#0958d9" },
  { key: "manpower", label: "Manpower", color: "#d46b08" },
  { key: "machinery", label: "Machinery", color: "#722ed1" },
];

interface ItemRow {
  key: string;
  name: string;
  code?: string;
  tag?: string;
  tagColor?: string;
  quantity: number;
  uom?: string;
  cost: number;
  taskCount: number;
}

const MonthBreakdownDrawer: React.FC<{
  open: boolean;
  month?: string | null;
  amounts?: PhasedAmounts | null;
  /** Whole scheduled programme, for the "share of programme" line. */
  programmeTotal?: number;
  projectId?: number | string;
  phaseId?: number | string;
  activityId?: number | string;
  // Branding + filter caption for the Excel export.
  projectName?: string;
  orgName?: string;
  orgLogoUrl?: string;
  filtersText?: string;
  onClose: () => void;
  getContainer?: () => HTMLElement;
}> = ({
  open,
  month,
  amounts,
  programmeTotal = 0,
  projectId,
  phaseId,
  activityId,
  projectName,
  orgName,
  orgLogoUrl,
  filtersText,
  onClose,
  getContainer,
}) => {
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  // One search box in the tab bar, applied to whichever tab is open. All rows
  // for the month are already in memory, so filtering is client-side.
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open || !month || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setDetail(null);
        setSearch("");
        const response = await threeMReportApis.getMonthlyPnlMonthDetail(
          projectId,
          month,
          { phaseId, activityId }
        );
        if (cancelled) return;
        if (response?.status) {
          setDetail(response.data || null);
        } else {
          message.error(response?.message || "Failed to load the month detail");
        }
      } catch (err) {
        console.error("Error loading month detail:", err);
        if (!cancelled) message.error("Failed to load the month detail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, month, projectId, phaseId, activityId]);

  const total = amounts?.total ?? 0;
  const share = programmeTotal > 0 ? (total / programmeTotal) * 100 : 0;

  const materials = useMemo<ItemRow[]>(
    () =>
      (detail?.materials || []).map((r: any, i: number) => ({
        key: `MAT-${r.materialId ?? i}`,
        name: r.materialName || "—",
        code: r.materialCode,
        quantity: num(r.monthQuantity),
        uom: r.uomName,
        cost: num(r.monthCost),
        taskCount: num(r.taskCount),
      })),
    [detail]
  );

  const manpower = useMemo<ItemRow[]>(
    () =>
      (detail?.manpower || []).map((r: any, i: number) => ({
        key: `MAN-${r.source}-${r.manpowerNormId ?? 0}-${r.laborTypeId ?? 0}-${r.laborCategoryId ?? 0}-${r.workUomId ?? 0}-${i}`,
        name: r.itemName || "—",
        code: r.itemCode,
        tag: r.source === "PRW" ? "PRW" : "Others",
        tagColor: r.source === "PRW" ? "blue" : "orange",
        quantity: num(r.monthQuantity),
        uom: r.uomName,
        cost: num(r.monthCost),
        taskCount: num(r.taskCount),
      })),
    [detail]
  );

  const machinery = useMemo<ItemRow[]>(
    () =>
      (detail?.machinery || []).map((r: any, i: number) => ({
        key: `MAC-${r.source}-${r.machineryNormId ?? 0}-${r.toolId ?? 0}-${r.workUomId ?? 0}-${r.deploymentNormId ?? 0}-${i}`,
        name: r.itemName || "—",
        code: r.itemCode,
        tag: r.head,
        // TOOLS orange, EQUIPMENT (deployment norms) green, norm heads geekblue
        // — same hues as the Machinery Cost Analysis chips.
        tagColor:
          r.head === "TOOLS"
            ? "orange"
            : r.head === "EQUIPMENT"
            ? "green"
            : "geekblue",
        quantity: num(r.monthQuantity),
        uom: r.uomName,
        cost: num(r.monthCost),
        taskCount: num(r.taskCount),
      })),
    [detail]
  );

  // Exports the WHOLE month, not the current search filter — the search is a
  // find-tool, and a workbook quietly missing rows because a filter was left
  // in the box would be a trap.
  const handleExport = async () => {
    if (!month || !amounts) return;
    setExporting(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportMonthBreakdownWorkbook } = await import(
        "./monthBreakdownWorkbook"
      );
      await exportMonthBreakdownWorkbook({
        month,
        projectName,
        orgName,
        logo,
        filtersText,
        amounts,
        programmeTotal,
        materials,
        manpower,
        machinery,
      });
    } catch (err) {
      console.error("Error exporting month breakdown:", err);
      message.error("Failed to export the month breakdown");
    } finally {
      setExporting(false);
    }
  };

  const splits = PILLARS.map((pillar) => ({
    ...pillar,
    value: amounts?.[pillar.key] ?? 0,
    pct: total > 0 ? ((amounts?.[pillar.key] ?? 0) / total) * 100 : 0,
  }));

  const filterRows = (rows: ItemRow[]): ItemRow[] => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.code, r.tag].some((v) => (v || "").toLowerCase().includes(q))
    );
  };

  const columns: ColumnsType<ItemRow> = [
    {
      title: "Item",
      dataIndex: "name",
      render: (_: any, row) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {row.tag ? (
              <Tag color={row.tagColor} style={{ marginRight: 0, fontSize: 11 }}>
                {row.tag}
              </Tag>
            ) : null}
            <span style={{ fontWeight: 500 }}>{row.name}</span>
          </div>
          {row.code ? (
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>{row.code}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: 130,
      align: "right",
      render: (value: number, row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(value)}
          {row.uom ? (
            <span style={{ color: "#8c8c8c", marginLeft: 4, fontSize: 12 }}>
              {row.uom}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      // Effective rate this month: what the item actually cost per unit once
      // the month's share is applied. Blank when there is no quantity to
      // divide by rather than showing a meaningless figure.
      title: "Rate",
      key: "rate",
      width: 120,
      align: "right",
      render: (_: any, row) => {
        const rate = perUnit(row.cost, row.quantity);
        if (rate === null) return <span style={{ color: "#bfbfbf" }}>—</span>;
        return (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtINR(rate)}
            {row.uom ? (
              <span style={{ color: "#8c8c8c", fontSize: 12 }}>/{row.uom}</span>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Cost",
      dataIndex: "cost",
      width: 120,
      align: "right",
      render: (value: number) => (
        <Tooltip title={fmtINR(value)}>
          <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {fmtINRCompact(value)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Tasks",
      dataIndex: "taskCount",
      width: 74,
      align: "right",
      render: (value: number) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
      ),
    },
  ];

  const itemTable = (allRows: ItemRow[], label: string) => {
    if (!allRows.length) {
      return <Empty description={`No ${label.toLowerCase()} in this month`} />;
    }
    const rows = filterRows(allRows);
    if (!rows.length) {
      return <Empty description={`No ${label.toLowerCase()} matches "${search}"`} />;
    }
    const sum = rows.reduce((s, r) => s + r.cost, 0);
    const filtered = rows.length !== allRows.length;
    return (
      <>
        <Table<ItemRow>
          className="customTable"
          rowKey="key"
          columns={columns}
          dataSource={rows}
          size="small"
          bordered
          pagination={false}
          scroll={{ y: 340 }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 600,
            color: "#022c57",
            padding: "8px 4px 0",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>
            {label} · {rows.length} item{rows.length === 1 ? "" : "s"}
            {filtered ? (
              <span style={{ fontWeight: 400, color: "#8c8c8c" }}>
                {" "}
                of {allRows.length}
              </span>
            ) : null}
          </span>
          <span>{fmtINR(sum)}</span>
        </div>
      </>
    );
  };

  return (
    <Drawer
      title={month ? fmtMonthLong(month) : "Month breakdown"}
      placement="right"
      width="min(880px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      extra={
        <Button
          size="small"
          icon={<FileExcelOutlined />}
          loading={exporting}
          disabled={loading || !amounts || total === 0}
          onClick={handleExport}
        >
          Export
        </Button>
      }
      {...(getContainer ? { getContainer } : {})}
    >
      {!amounts || total === 0 ? (
        <Empty description="Nothing scheduled in this month" />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <span>
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#022c57",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtINR(total)}
              </span>
              <span style={{ fontSize: 12, color: "#8c8c8c", marginLeft: 8 }}>
                month total
              </span>
            </span>
            {programmeTotal > 0 ? (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                {share.toFixed(1)}% of the {fmtINRCompact(programmeTotal)}{" "}
                scheduled programme
              </span>
            ) : null}
          </div>

          {/* One stacked bar rather than a progress row per M: the same
              proportions in a fraction of the height, which matters because
              the item tables below are the reason the drawer is open. */}
          <div
            style={{
              display: "flex",
              height: 10,
              borderRadius: 5,
              overflow: "hidden",
              background: "#f0f0f0",
              marginBottom: 8,
            }}
          >
            {splits
              .filter((sp) => sp.pct > 0)
              .map((sp) => (
                <Tooltip
                  key={sp.key}
                  title={`${sp.label} · ${fmtINR(sp.value)} · ${sp.pct.toFixed(1)}%`}
                >
                  <div style={{ width: `${sp.pct}%`, background: sp.color }} />
                </Tooltip>
              ))}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 18,
              fontSize: 12,
              marginBottom: 4,
            }}
          >
            {splits.map((sp) => (
              <span
                key={sp.key}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: sp.color,
                    display: "inline-block",
                  }}
                />
                <span style={{ color: "#595959" }}>{sp.label}</span>
                <Tooltip title={fmtINR(sp.value)}>
                  <b style={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtINRCompact(sp.value)}
                  </b>
                </Tooltip>
                <span style={{ color: "#8c8c8c" }}>{sp.pct.toFixed(1)}%</span>
              </span>
            ))}
          </div>

          <div style={{ marginTop: 18 }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "48px 0" }}>
                <Spin />
              </div>
            ) : (
              <Tabs
                size="small"
                tabBarExtraContent={
                  <Input
                    allowClear
                    size="small"
                    placeholder="Search items"
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 220 }}
                  />
                }
                items={[
                  {
                    key: "materials",
                    label: `Materials (${materials.length})`,
                    children: itemTable(materials, "Material"),
                  },
                  {
                    key: "manpower",
                    label: `Manpower (${manpower.length})`,
                    children: itemTable(manpower, "Manpower"),
                  },
                  {
                    key: "machinery",
                    label: `Machinery (${machinery.length})`,
                    children: itemTable(machinery, "Machinery"),
                  },
                ]}
              />
            )}
          </div>
        </>
      )}
    </Drawer>
  );
};

export default MonthBreakdownDrawer;
